<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ReportsAPI from 'dashboard/api/reports';
import { endOfDay, startOfDay, subDays, startOfMonth, endOfMonth, getUnixTime } from 'date-fns';
import { formatDuration } from 'shared/helpers/timeHelper';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();

const isLoading = ref(true);
const metrics = ref({});
const selectedDateRange = ref('7'); // '1', '7', '30', 'month'

const dateRanges = [
  { label: 'Today', value: '1' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 Days', value: '7' },
  { label: 'Last 30 Days', value: '30' },
  { label: 'This Month', value: 'month' },
];

const fetchPerformanceData = async () => {
  isLoading.value = true;
  try {
    let fromDate, toDate;
    const now = new Date();

    if (selectedDateRange.value === '1') {
      fromDate = startOfDay(now);
      toDate = endOfDay(now);
    } else if (selectedDateRange.value === 'yesterday') {
      const yesterday = subDays(now, 1);
      fromDate = startOfDay(yesterday);
      toDate = endOfDay(yesterday);
    } else if (selectedDateRange.value === '7') {
      fromDate = startOfDay(subDays(now, 6));
      toDate = endOfDay(now);
    } else if (selectedDateRange.value === '30') {
      fromDate = startOfDay(subDays(now, 29));
      toDate = endOfDay(now);
    } else if (selectedDateRange.value === 'month') {
      fromDate = startOfMonth(now);
      toDate = endOfMonth(now);
    }

    const since = getUnixTime(fromDate);
    const until = getUnixTime(toDate);

    const response = await ReportsAPI.getSummary(
      since,
      until,
      'agent',
      props.id
    );

    metrics.value = response.data;
  } catch (error) {
    useAlert(t('REPORT.FETCH_ERROR') || 'Error fetching report');
  } finally {
    isLoading.value = false;
  }
};

const formatTimeVal = (time) => {
  if (!time || time === '0') return '0s';
  return formatDuration(time);
};

onMounted(() => {
  fetchPerformanceData();
});
</script>

<template>
  <div class="flex flex-col gap-6 p-4">
    <div class="flex items-center justify-between">
      <select v-model="selectedDateRange" @change="fetchPerformanceData" class="w-48 text-sm bg-white dark:bg-n-solid-2 border border-n-weak rounded-md px-3 py-1.5 focus:ring-1 focus:ring-n-brand focus:border-n-brand outline-none">
        <option v-for="range in dateRanges" :key="range.value" :value="range.value">
          {{ range.label }}
        </option>
      </select>
    </div>

    <div v-if="isLoading" class="flex justify-center py-12">
      <div class="flex flex-col items-center gap-2">
        <div class="w-6 h-6 border-2 border-t-n-brand border-n-weak rounded-full animate-spin" />
        <span class="text-sm text-n-slate-11">Loading metrics...</span>
      </div>
    </div>

    <div v-else class="grid grid-cols-2 gap-4">
      <div class="p-4 bg-white dark:bg-n-solid-2 rounded-xl shadow-sm border border-n-weak flex flex-col gap-1 transition-transform hover:-translate-y-0.5">
        <span class="text-xs font-medium uppercase tracking-wider text-n-slate-10">Conversations</span>
        <span class="text-2xl font-semibold text-n-slate-12">{{ metrics.conversations_count || 0 }}</span>
      </div>
      <div class="p-4 bg-white dark:bg-n-solid-2 rounded-xl shadow-sm border border-n-weak flex flex-col gap-1 transition-transform hover:-translate-y-0.5">
        <span class="text-xs font-medium uppercase tracking-wider text-n-slate-10">Resolutions</span>
        <span class="text-2xl font-semibold text-n-slate-12">{{ metrics.resolutions_count || 0 }}</span>
      </div>
      <div class="p-4 bg-white dark:bg-n-solid-2 rounded-xl shadow-sm border border-n-weak flex flex-col gap-1 transition-transform hover:-translate-y-0.5">
        <span class="text-xs font-medium uppercase tracking-wider text-n-slate-10">Incoming Msgs</span>
        <span class="text-2xl font-semibold text-n-slate-12">{{ metrics.incoming_messages_count || 0 }}</span>
      </div>
      <div class="p-4 bg-white dark:bg-n-solid-2 rounded-xl shadow-sm border border-n-weak flex flex-col gap-1 transition-transform hover:-translate-y-0.5">
        <span class="text-xs font-medium uppercase tracking-wider text-n-slate-10">Outgoing Msgs</span>
        <span class="text-2xl font-semibold text-n-slate-12">{{ metrics.outgoing_messages_count || 0 }}</span>
      </div>
      <div class="p-4 bg-white dark:bg-n-solid-2 rounded-xl shadow-sm border border-n-weak flex flex-col gap-1 transition-transform hover:-translate-y-0.5">
        <span class="text-xs font-medium uppercase tracking-wider text-n-slate-10">Avg First Response</span>
        <span class="text-2xl font-semibold text-n-slate-12">{{ formatTimeVal(metrics.avg_first_response_time) }}</span>
      </div>
      <div class="p-4 bg-white dark:bg-n-solid-2 rounded-xl shadow-sm border border-n-weak flex flex-col gap-1 transition-transform hover:-translate-y-0.5">
        <span class="text-xs font-medium uppercase tracking-wider text-n-slate-10">Avg Resolution</span>
        <span class="text-2xl font-semibold text-n-slate-12">{{ formatTimeVal(metrics.avg_resolution_time) }}</span>
      </div>
    </div>
  </div>
</template>
